import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isStandalone, isInstallOfferBlocked } from '@/pwa/pwaEnvironment';

const VAPID_PUBLIC_KEY = 'BDc0vb90FpN88q-J7wqTkS6CEwk0l_ESuPk0r0EsWmwV7vV3ipap98TbbMChNQGoC8nsY03K5HLBW6_J6MR6ieY';

function toUint8(base64: string) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export type WebPushState = 'unsupported' | 'needs-install' | 'denied' | 'off' | 'on' | 'loading';

const isIOS = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

// Estado compartilhado entre todas as instâncias do hook.
let sharedState: WebPushState = 'loading';
const listeners = new Set<(s: WebPushState) => void>();
const setShared = (s: WebPushState) => { sharedState = s; listeners.forEach((l) => l(s)); };

async function waitForRegistration(timeoutMs = 5000): Promise<ServiceWorkerRegistration | undefined> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  const timeout = new Promise<undefined>((r) => setTimeout(() => r(undefined), timeoutMs));
  const ready = await Promise.race([navigator.serviceWorker.ready, timeout]);
  return ready ?? (await navigator.serviceWorker.getRegistration());
}

let refreshing: Promise<void> | null = null;
async function doRefresh() {
  if (typeof window === 'undefined') return;
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!supported) { setShared(isIOS() && !isStandalone() ? 'needs-install' : 'unsupported'); return; }
  if (isInstallOfferBlocked()) { setShared('unsupported'); return; }
  if (Notification.permission === 'denied') { setShared('denied'); return; }
  if (Notification.permission === 'default') {
    // Sem permissão ainda não pode haver inscrição: 'off' mesmo sem SW registrado.
    setShared('off');
    return;
  }
  const reg = await waitForRegistration();
  if (!reg) { setShared('unsupported'); return; }
  const sub = await reg.pushManager.getSubscription();
  setShared(sub ? 'on' : 'off');
}
function refreshShared() {
  if (!refreshing) {
    refreshing = doRefresh().catch(() => setShared('unsupported')).finally(() => { refreshing = null; });
  }
  return refreshing;
}

export function useWebPush(userId?: string) {
  const [state, setState] = useState<WebPushState>(sharedState);

  useEffect(() => {
    listeners.add(setState);
    setState(sharedState);
    refreshShared();
    return () => { listeners.delete(setState); };
  }, []);

  const refresh = useCallback(() => doRefresh().catch(() => setShared('unsupported')), []);

  const enable = useCallback(async () => {
    if (!userId) return false;
    setShared('loading');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { await refresh(); return false; }
      if (!(await navigator.serviceWorker.getRegistration()) && !isInstallOfferBlocked()) {
        await navigator.serviceWorker.register('/sw.js');
      }
      const reg = await waitForRegistration(8000);
      if (!reg) throw new Error('service worker indisponível');
      const sub = (await reg.pushManager.getSubscription())
        ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toUint8(VAPID_PUBLIC_KEY) }));
      const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      // Mesmo aparelho pode ter sido de outra pessoa: remove o registro antigo dela antes.
      await supabase.from('push_subscriptions').delete().eq('endpoint', json.endpoint).select('id');
      const { error } = await supabase.from('push_subscriptions').insert({
        user_id: userId, endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth,
        user_agent: navigator.userAgent.slice(0, 300),
      });
      if (error && error.code !== '23505') throw error;
      await refresh();
      return !error || error.code === '23505';
    } catch (e) {
      console.error('[push] enable', e);
      await refresh();
      return false;
    }
  }, [userId, refresh]);

  const disable = useCallback(async () => {
    setShared('loading');
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint).select('id');
        await sub.unsubscribe();
      }
    } finally { await refresh(); }
  }, [refresh]);

  return { state, enable, disable };
}
